import React from "react";
import headingStyles from "./ModalHeadingStyles";

const MathematicsAndArtModalContent = () => (
  <div style={{ textAlign: 'left', fontFamily: 'Raleway, sans-serif' }}>
    <h3 style={headingStyles.h3}>Mathematics and Art TED-Ed Series</h3>
    <hr />
    <div style={{ fontWeight: 'bold', color: '#000' }}>Content Design & Storytelling</div>
    <div>6 weeks (2014)</div>
    <p><em>Wrote and co-created three animated TED-Ed lessons exploring the connections between mathematics and art, music, and literature</em></p>
    <hr />
    <div><strong style={{ fontWeight: 800 }}>Project Role:</strong> TED-Ed Author and Educator</div>
    <div><strong style={{ fontWeight: 800 }}>Platforms:</strong> Microsoft Word (scriptwriting), Adobe Illustrator (visual aids), TED-Ed</div>
    <div><strong style={{ fontWeight: 800 }}>Deliverables:</strong> 3 TED-Ed videos + accompanying lesson materials</div>
    <div><strong style={{ fontWeight: 800 }}>Timeline:</strong> 6 weeks</div>
    <hr />

    <h4 style={headingStyles.h4}>Project Overview</h4>
    <p>
    I wrote and co-developed three TED-Ed animated lessons on how math shows up in unexpected places—art, music, and literature. One of these videos, on Van Gogh’s <em>Starry Night</em>, was later named <a href="http://blog.ed.ted.com/2015/10/09/top-5-ted-ed-lessons-of-all-time/" target="_blank" rel="noreferrer"><strong>“Best TED-Ed Lesson of All Time”</strong></a> and has received <strong>over 9 million views</strong> on YouTube.
    </p>
    <p>
    Collaborating with editors, producers, animators, and fact-checkers across time zones, I turned abstract math concepts into clear, engaging scripts supported by visual references. From sinusoidal sound waves to Shakespearean authorship debates, each video was designed to spark curiosity and make math feel relevant to a global student audience.
    </p>

    <h4 style={headingStyles.h4}>The Solution</h4>
    <p>
    I developed three narrative-driven math lessons that connected big ideas in the arts to core mathematical concepts:
    </p>
    <ul>
      <li><a href="https://ed.ted.com/lessons/the-unexpected-math-behind-van-gogh-s-starry-night-natalya-st-clair" target="_blank" rel="noreferrer">The Unexpected Math Behind Van Gogh’s Starry Night</a> — visual art & turbulence</li>
      <li><a href="https://youtu.be/M51Bm9sNazM?si=cv4vE00xiZL2-JmA" target="_blank" rel="noreferrer">The Math Behind Music</a> — sound waves & sinusoidal functions</li>
      <li><a href="https://ed.ted.com/lessons/did-shakespeare-write-his-plays-natalya-st-clair-and-aaron-williams" target="_blank" rel="noreferrer">Did Shakespeare Write His Plays?</a> — authorship attribution & frequency analysis</li>
    </ul>
    <p>
    Each script was paired with animation-ready visual diagrams to bring the math to life and ensure clarity for a student audience worldwide.
    </p>

    <h4 style={headingStyles.h4}>Aligning Goals</h4>
    <p>
    This project started as a single script, but after early conversations with TED-Ed producers, we expanded it into a three-part series. I learned to adapt complex mathematical ideas into joyful, globally accessible lessons—while navigating the real-world logistics of animation, editorial feedback, and asynchronous production cycles.
    </p>

    <h4 style={headingStyles.h4}>Discover</h4>
    <ul>
      <li>Researched math applications across disciplines: literature, music theory, and visual analysis</li>
      <li>Crafted scripts that translated abstract math into engaging narratives with visual storytelling potential</li>
      <li>Collaborated with TED-Ed’s team of editors, animators, and producers to ensure clarity and accuracy</li>
    </ul>

    <h4 style={headingStyles.h4}>Define</h4>
    <ul>
      <li>Iterated heavily on the Shakespeare video — it took multiple rounds to land on the right story structure</li>
      <li>Balanced mathematical accuracy with wide accessibility, especially for global audiences aged 13–18</li>
      <li>Developed visuals using Illustrator to help the animation teams bring complex math ideas to life</li>
    </ul>

    <h4 style={headingStyles.h4}>Develop</h4>
    <ul>
      <li>Wrote and revised 3 full-length TED-Ed scripts</li>
      <li>Created original math diagrams and sketches used as visual references for animation teams</li>
      <li>Worked across production teams in multiple time zones, navigating asynchronous workflows and editorial cycles</li>
    </ul>

    <h4 style={headingStyles.h4}>Deliver</h4>
    <ul>
      <li><strong>9M+ views</strong> on the Van Gogh video alone</li>
      <li>Named <strong>“Best TED-Ed Lesson of All Time”</strong> in 2015</li>
      <li>Project inspired further interdisciplinary math-art curriculum work</li>
      <li>Learned the joy (and challenge) of writing educational content for global learners</li>
      <li>Grateful to collaborate with TED-Ed visionaries like Logan Smalley and Alex Gendler</li>
    </ul>
  </div>
);

export default MathematicsAndArtModalContent;